/* eslint-disable no-undef */
import React, { useEffect, useState, useCallback } from 'react';
import {
  AnalyticalTable,
  Button,
  Dialog,
  Title,
  BusyIndicator,
  Input,
  Bar,
  Form,
  FormItem,
  FormGroup,
  Text,
  TextArea,
  Label,
  Select,
  Option,
  Icon
} from '@ui5/webcomponents-react';
import axiosInstance from '../utils/axiosInstance';
import useAutoTranslate from '../hooks/useAutoTranslate';
import Swal from 'sweetalert2';

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true
});

const toApiStatus = (status) => {
  const normalized = (status || '').toString().trim().toUpperCase();
  if (normalized === 'ACTIVO') return 'ACTIVE';
  if (normalized === 'INACTIVO') return 'INACTIVE';
  return normalized;
};

const SupplierList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showDialog, setShowDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [formData, setFormData] = useState({
    supplierName: '',
    supplierRut: '',
    email: '',
    status: 'ACTIVE',
    paymentTermDays: 30,
    paymentTermDescription: '',
    paymentTermType: 'STANDARD',
    paymentTermDiscount: 0,
    deliveryConditions: '',
    addresses: []
  });
  const [formErrors, setFormErrors] = useState({});

  const { autoT } = useAutoTranslate();

  const fetchSuppliers = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`suppliers?page=${page}&size=${pageSize}`);
      if (response.data.records.code === "SUCCESS") {
        const normalized = (response.data.records.data || []).map((s) => ({
          ...s,
          status: toApiStatus(s.status)
        }));
        setData(normalized);
      } else {
        setError(response.data.records.message);
        Toast.fire({ icon: 'error', title: response.data.records.message || autoT('Error al cargar los proveedores') });
      }
    } catch (err) {
      setError('Error al cargar los proveedores: ' + (err.message || 'Error desconocido'));
      Toast.fire({ icon: 'error', title: autoT('Error al cargar los proveedores') });
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    fetchSuppliers(currentPage);
  }, [currentPage, fetchSuppliers]);

  useEffect(() => {
    let filtered = [...data];
    
    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(supplier => 
        supplier.supplierName?.toLowerCase().includes(lowerCaseSearchTerm) ||
        supplier.supplierRut?.toLowerCase().includes(lowerCaseSearchTerm) ||
        supplier.email?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
    
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(supplier => toApiStatus(supplier.status) === statusFilter);
    }
    
    setFilteredData(filtered);
  }, [data, searchTerm, statusFilter]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.detail.selectedOption.value);
  };

  const resetForm = () => {
    setFormData({
      supplierName: '',
      supplierRut: '',
      email: '',
      status: 'ACTIVE',
      paymentTermDays: 30,
      paymentTermDescription: '',
      paymentTermType: 'STANDARD',
      paymentTermDiscount: 0,
      deliveryConditions: '',
      addresses: []
    });
    setFormErrors({});
  };

  const handleCreateClick = () => {
    resetForm();
    setSelectedSupplier(null);
    setIsEditing(false);
    setShowDialog(true);
  };

  const handleEditClick = (supplier) => {
    setSelectedSupplier(supplier);
    setFormData({
      supplierName: supplier.supplierName || '',
      supplierRut: supplier.supplierRut || '',
      email: supplier.email || '',
      status: toApiStatus(supplier.status || 'ACTIVE'),
      paymentTermDays: supplier.paymentTermDays || 30,
      paymentTermDescription: supplier.paymentTermDescription || '',
      paymentTermType: supplier.paymentTermType || 'STANDARD',
      paymentTermDiscount: supplier.paymentTermDiscount || 0,
      deliveryConditions: supplier.deliveryConditions || '',
      addresses: supplier.addresses || []
    });
    setFormErrors({});
    setIsEditing(true);
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      let response;
      if (isEditing) {
        const payload = {
          ...formData,
          status: toApiStatus(formData.status),
          modifiedAt: new Date().toISOString()
        };
        response = await axiosInstance.put(`suppliers/${selectedSupplier.id}`, payload);
      } else {
        const payload = {
          ...formData,
          status: toApiStatus(formData.status),
          createdAt: new Date().toISOString(),
          modifiedAt: new Date().toISOString()
        };
        response = await axiosInstance.post('suppliers', payload);
      }
      
      if (response.status === 200 || response.status === 201) {
        fetchSuppliers(currentPage);
        setShowDialog(false);
        Toast.fire({
          icon: 'success',
          title: isEditing ? autoT('Proveedor actualizado exitosamente') : autoT('Proveedor creado exitosamente')
        });
      } else {
        const action = isEditing ? 'actualizar' : 'crear';
        setError(`Error al ${action} el proveedor: ` + (response.data.message || 'Error desconocido'));
        Toast.fire({ icon: 'error', title: autoT('Error al {{action}} el proveedor', { action }) });
      }
    } catch (err) {
      const action = isEditing ? 'actualizar' : 'crear';
      setError(`Error al ${action} el proveedor: ` + (err.message || 'Error desconocido'));
      Toast.fire({ icon: 'error', title: autoT('Error al {{action}} el proveedor', { action }) });
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (event) => {
    const { name } = event.target;
    const value = event.detail.selectedOption.getAttribute('value');
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.supplierName) {
      errors.supplierName = 'El nombre del proveedor es obligatorio';
    }
    if (!formData.supplierRut) {
      errors.supplierRut = 'El RUT del proveedor es obligatorio';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'El formato del email no es válido';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const columns = [
    {
      Header: 'Nombre del Proveedor',
      accessor: 'supplierName',
    },
    {
      Header: 'RUT',
      accessor: 'supplierRut',
    },
    {
      Header: 'Email',
      accessor: 'email',
    },
    {
      Header: 'Estado',
      accessor: 'status',
      Cell: ({ value }) => {
        const statusUpper = toApiStatus(value);
        const isActive = statusUpper === 'ACTIVE';
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Icon 
              name={isActive ? "status-positive" : "status-negative"} 
              style={{ marginRight: '8px', color: isActive ? '#28a745' : '#dc3545' }} 
            />
            <Text>{isActive ? 'Activo' : 'Inactivo'}</Text>
          </div>
        );
      },
    },
    {
      Header: 'Condiciones de entrega',
      accessor: 'deliveryConditions',
      Cell: ({ cell }) => (
        <Text style={{ 
          fontSize: '0.95rem',
          color: cell.value ? '#2c3e50' : '#95a5a6',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          {/* <Icon name="payment-approval" style={{ fontSize: '1.1rem' }} /> */}
          {cell.value || 'No especificado'}
        </Text>
      ),
      width: 220
    },
    {
      Header: 'Acciones',
      id: 'actions',
      Cell: ({ row }) => (
        <Button 
          design="Transparent" 
          onClick={() => handleEditClick(row.original)}
        >
          Editar
        </Button>
      )
    }
  ];

  return (
    <>
      <div style={{ padding: '1rem' }}>
        <Title level="H2">{autoT('Gestión de Proveedores')}</Title>
        {error && <div style={{ color: 'red' }}>{error}</div>}
        <Bar
          startContent={
            <div style={{ display: 'flex', gap: '1rem' }}>
              <Input
                placeholder={autoT('Buscar proveedor...')}
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <Select
                onChange={handleStatusFilterChange}
                value={statusFilter}
              >
                <Option value="ALL">{autoT('Todos los estados')}</Option>
                <Option value="ACTIVE">{autoT('Activos')}</Option>
                <Option value="INACTIVE">{autoT('Inactivos')}</Option>
              </Select>
            </div>
          }
          endContent={
            <Button design="Emphasized" onClick={handleCreateClick}>
              {autoT('Crear Proveedor')}
            </Button>
          }
        />
        {loading ? (
          <BusyIndicator active />
        ) : (
          <AnalyticalTable
            data={filteredData}
            columns={columns}
            selectionMode="None"
            style={{ marginTop: '1rem' }}
            visibleRows={10}
            minRows={10}
            alternateRowColor
            onRowClick={(e) => {
              if (e && e.detail && e.detail.row && e.detail.row.original) {
                handleEditClick(e.detail.row.original);
              }
            }}
          />
        )}
      </div>

      {/* Dialog Unificado para Crear/Editar Proveedor */}
      <Dialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        header={
          <Bar
            startContent={
              <Title level="H4">
                {isEditing ? autoT('Editar Proveedor') : autoT('Crear Nuevo Proveedor')}
              </Title>
            }
          />
        }
        footer={
          <Bar
            endContent={
              <>
                <Button 
                  design="Emphasized" 
                  onClick={handleSubmit}
                >
                  {isEditing ? autoT('Guardar Cambios') : autoT('Crear Proveedor')}
                </Button>
                <Button 
                  design="Transparent" 
                  onClick={() => setShowDialog(false)}
                >
                  {autoT('Cancelar')}
                </Button>
              </>
            }
          />
        }
        style={{ width: '800px' }}
      >
        <Form style={{ padding: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {/* Columna 1 - Información Básica */}
            <FormGroup titleText={autoT('Información Básica')}>
              <FormItem labelContent={<Label>{autoT('Nombre del Proveedor')}</Label>}>
                <Input 
                  name="supplierName" 
                  value={formData.supplierName}
                  onChange={handleInputChange}
                  valueState={formErrors.supplierName ? "Error" : "None"}
                  valueStateMessage={formErrors.supplierName}
                  required
                />
              </FormItem>
              <FormItem labelContent={<Label>{autoT('RUT')}</Label>}>
                <Input 
                  name="supplierRut" 
                  value={formData.supplierRut}
                  onChange={handleInputChange}
                  valueState={formErrors.supplierRut ? "Error" : "None"}
                  valueStateMessage={formErrors.supplierRut}
                  required
                />
              </FormItem>
              <FormItem labelContent={<Label>{autoT('Email')}</Label>}>
                <Input 
                  name="email" 
                  value={formData.email}
                  onChange={handleInputChange}
                  type="email"
                  valueState={formErrors.email ? "Error" : "None"}
                  valueStateMessage={formErrors.email}
                />
              </FormItem>
            </FormGroup>

            {/* Columna 2 - Configuración Comercial */}
            <FormGroup titleText={autoT('Configuración Comercial')}>
              <FormItem labelContent={<Label>{autoT('Estado')}</Label>}>
                <Select 
                  name="status" 
                  value={formData.status}
                  onChange={handleSelectChange}
                >
                  <Option value="ACTIVE">Activo</Option>
                  <Option value="INACTIVE">Inactivo</Option>
                </Select>
              </FormItem>
              <FormItem labelContent={<Label>{autoT('Días de Pago')}</Label>}>
                <Input 
                  name="paymentTermDays" 
                  value={formData.paymentTermDays}
                  onChange={handleInputChange}
                  type="number"
                />
              </FormItem>
              <FormItem labelContent={<Label>{autoT('Tipo de Términos de Pago')}</Label>}>
                <Select 
                  name="paymentTermType" 
                  value={formData.paymentTermType}
                  onChange={handleSelectChange}
                >
                  <Option value="STANDARD">Estándar</Option>
                  <Option value="PREPAID">Prepago</Option>
                  <Option value="CREDIT">Crédito</Option>
                  <Option value="COD">Contra Entrega</Option>
                </Select>
              </FormItem>
            </FormGroup>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
            {/* Columna 3 - Términos de Pago */}
            <FormGroup titleText={autoT('Términos de Pago')}>
              <FormItem labelContent={<Label>{autoT('Descuento (%)')}</Label>}>
                <Input 
                  name="paymentTermDiscount" 
                  value={formData.paymentTermDiscount}
                  onChange={handleInputChange}
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                />
              </FormItem>
              <FormItem labelContent={<Label>{autoT('Descripción Términos de Pago')}</Label>}>
                <TextArea
                  name="paymentTermDescription"
                  value={formData.paymentTermDescription}
                  onChange={handleInputChange}
                  rows={3}
                  style={{
                    width: '100%',
                    resize: 'vertical',
                    minHeight: '100px'
                  }}
                />
              </FormItem>
            </FormGroup>

            {/* Columna 4 - Condiciones de Entrega */}
            <FormGroup titleText={autoT('Condiciones de Entrega')}>
              <FormItem labelContent={<Label>{autoT('Condiciones de Entrega')}</Label>}>
                <TextArea
                  name="deliveryConditions"
                  value={formData.deliveryConditions}
                  onChange={handleInputChange}
                  rows={5}
                  style={{
                    width: '100%',
                    resize: 'vertical',
                    minHeight: '120px'
                  }}
                  placeholder={autoT('Especifique las condiciones de entrega, horarios, restricciones, etc.')}
                />
              </FormItem>
            </FormGroup>
          </div>
        </Form>
      </Dialog>
    </>
  );
};

export default SupplierList;